import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  getLeads,
  getLeadsStatus,
  setLeadsList,
} from '../../../store/slices/leadsSlice';
import { extractList } from '../../../utils/response';
import { getErrorMessage } from '../../../utils/errorHandler';
import { getUsersList, getUsersByRole } from '../../../store/slices/userSlice';
import { buildQueryParams } from '../utils';

export const useLeads = (canViewListLead = true) => {
  const dispatch = useDispatch();
  const leadsState = useSelector((state) => state.leads);
  const usersState = useSelector((state) => state.users);

  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });
  const [noActivity, setNoActivity] = useState({
    no_activity_since_days: '',
  });
  const [filters, setFilters] = useState({
    status_id: '',
    assignee_id: '',
    author_id: '',
  });
  const [sort, setSort] = useState({
    sort_by: 'updated_at',
    sort_direction: 'desc',
  });
  const [frontSellerOptions, setFrontsellerOptions] = useState([]);
  const [processingId, setProcessingId] = useState(null);

  const leadsResponse = leadsState.leads;
  const leads = extractList(leadsResponse);
  const tableLoading = leadsState.loading;
  const statusOptions = leadsState.statuses?.data?.list ?? leadsState.statuses?.data?.data?.list ?? [];
  const usersList = usersState.users?.list ?? usersState.users ?? [];

  const fetchLeads = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize, search = searchValue) => {
      if (!canViewListLead) {
        toast.error('You do not have permission to view leads.');
        return;
      }
      const params = buildQueryParams(
        { page, pageSize, total: pagination.total },
        filters,
        sort,
        noActivity,
        search
      );
      try {
        const res = await dispatch(getLeads(params)).unwrap();
        if (res?.success && res?.data?.pagination) {
          setPagination((p) => ({ ...p, total: res.data.pagination?.total || 0 }));
        }
      } catch (e) {
        toast.error(getErrorMessage(e) || 'Failed to fetch leads');
      }
    },
    [canViewListLead, dispatch, filters, sort, noActivity, pagination.total, searchValue]
  );

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        await Promise.all([
          dispatch(getLeadsStatus({ 'conditions[model]': 'Lead' })),
          dispatch(getUsersList({})),
        ]);
      } catch (e) {
        toast.error('Failed to load metadata');
      }
    };

    const fetchUser = async () => {
      try {
        const res = await dispatch(getUsersByRole('Sales User')).unwrap();
        const raw = res?.data ?? res;
        const list = extractList(raw);
        const salesList = list.map((u) => ({
          id: u?.uuid ?? u?.id,
          name: u?.name,
        }));
        setFrontsellerOptions(salesList);
      } catch (error) {
        console.error('Error fetching users data:', error);
      }
    };

    fetchUser();
    fetchMeta();
  }, [dispatch]);

  useEffect(() => {
    if (!canViewListLead) return;
    fetchLeads();
  }, [canViewListLead, pagination.page, pagination.pageSize, filters, sort, noActivity]);

  useEffect(() => {
    if (!canViewListLead) return;
    const timer = setTimeout(() => {
      setPagination((p) => ({ ...p, page: 0 }));
      fetchLeads(0, pagination.pageSize, searchValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSortChange = (field) => {
    setSort((prev) => ({
      sort_by: field,
      sort_direction:
        prev.sort_by === field && prev.sort_direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const clearFilters = () => {
    setFilters({ status_id: '', assignee_id: '', author_id: '' });
    setSort({ sort_by: 'updated_at', sort_direction: 'desc' });
    setNoActivity({ no_activity_since_days: '' });
  };

  const handleNoActivityChange = (value) => {
    setNoActivity({ no_activity_since_days: value });
  };

  const getActiveFiltersCount = () => {
    const filterCount = Object.values(filters).filter(
      (v) => v !== '' && v !== null && v !== undefined
    ).length;
    return filterCount + (noActivity.no_activity_since_days ? 1 : 0);
  };

  const handleSearchChange = (e) => setSearchValue(e.target.value);

  const handleClearSearch = () => {
    setSearchValue('');
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const handlePageChange = (_, newPage) =>
    setPagination((p) => ({ ...p, page: newPage }));

  const handleRowsPerPageChange = (e) => {
    const sz = parseInt(e.target.value, 10);
    setPagination({ page: 0, pageSize: sz, total: pagination.total });
  };

  const setLeads = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setLeadsList(updater));
    }
  };

  return {
    leads,
    tableLoading,
    searchValue,
    pagination,
    filters,
    sort,
    noActivity,
    statusOptions,
    usersList,
    frontSellerOptions,
    processingId,
    setProcessingId,
    setLeads,
    fetchLeads,
    handleSearchChange,
    handleClearSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleSortChange,
    handleNoActivityChange,
    clearFilters,
    getActiveFiltersCount,
  };
};
